import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Instagram } from "lucide-react";
import { cn } from "../lib/api";
import { Button, Sheet } from "./ui";
import { toStory } from "../lib/reportImage";

const FORMATS = [
  { key: "post", label: "Post (4:5)" },
  { key: "story", label: "Story & Status (9:16)" },
];

/**
 * Builds a shareable image (makeImage → PNG Blob) and hands it to the phone's share sheet —
 * Instagram (Feed / Story), WhatsApp Status, anything. Instagram has no "post from a website"
 * API, so this is how apps do it. Post = 1080x1350, Story & Status = 1080x1920.
 */
export default function InstagramShareSheet({ open, onClose, title = "Share as image", makeImage, caption, fileName = "cricso" }) {
  const [format, setFormat] = useState("post");
  const [images, setImages] = useState({}); // format -> { blob, url }

  useEffect(() => {
    if (!open) return;
    let alive = true;
    const made = [];
    setImages({});
    (async () => {
      const card = await makeImage();
      if (!alive || !card) return;
      const add = (key, blob) => { const url = URL.createObjectURL(blob); made.push(url); setImages((m) => ({ ...m, [key]: { blob, url } })); };
      add("post", card);
      const story = await toStory(card);
      if (alive && story) add("story", story);
    })();
    return () => { alive = false; made.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line
  }, [open]);

  const img = images[format];
  const name = `${fileName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${format === "story" ? "-story" : ""}.png`;
  const file = img && new File([img.blob], name, { type: "image/png" });
  const canShare = !!(file && navigator.canShare?.({ files: [file] }));

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(caption); return true; } catch { return false; }
  };

  const share = async () => {
    // Instagram drops text sent with an image, so the caption goes to the clipboard to paste
    const copied = caption ? await copyCaption() : false;
    try {
      await navigator.share({ files: [file] });
      if (copied) toast.success("Caption copied — paste it in Instagram");
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Couldn't open sharing — save the image instead");
    }
  };

  const save = () => {
    const a = document.createElement("a");
    a.href = img.url; a.download = name; a.click();
    toast.success("Image saved — post it to Instagram or your WhatsApp Status");
  };

  return (
    <Sheet open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-1 p-1 bg-black/[0.04] rounded-xl text-xs font-semibold" role="tablist">
          {FORMATS.map((f) => (
            <button key={f.key} role="tab" aria-selected={format === f.key} onClick={() => setFormat(f.key)}
              className={cn("h-9 rounded-lg", format === f.key ? "bg-white shadow-sm text-ink" : "text-ink-muted")}
              data-testid={`ig-format-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className={cn("mx-auto rounded-2xl overflow-hidden bg-black/5 grid place-items-center",
          format === "story" ? "aspect-[9/16] max-h-[52vh]" : "aspect-[4/5] max-h-[52vh]")}>
          {img ? <img src={img.url} alt="Shareable summary" className="w-full h-full object-contain" data-testid="ig-preview" />
               : <span className="text-sm text-ink-muted px-6 text-center">Creating image…</span>}
        </div>
        {canShare ? (
          <button
            type="button"
            onClick={share}
            className="w-full h-12 rounded-2xl text-white font-semibold flex items-center justify-center gap-2 btn-press
              bg-[linear-gradient(45deg,#F58529,#DD2A7B_45%,#8134AF_75%,#515BD4)]"
            data-testid="ig-share"
          >
            <Instagram size={18} /> {format === "story" ? "Share to Story / Status" : "Share to Instagram"}
          </button>
        ) : (
          <p className="text-xs text-ink-muted text-center">
            Sharing images to apps works on phones. Here, save the image and post it from Instagram or WhatsApp.
          </p>
        )}
        <div className={cn("grid gap-2", caption ? "grid-cols-2" : "grid-cols-1")}>
          <Button variant="outline" size="sm" className="h-11" onClick={save} disabled={!img} data-testid="ig-save">
            <Download size={16} /> Save image
          </Button>
          {caption && (
            <Button variant="outline" size="sm" className="h-11"
              onClick={async () => ((await copyCaption()) ? toast.success("Caption copied") : toast.error("Couldn't copy"))}
              data-testid="ig-caption">
              <Copy size={16} /> Copy caption
            </Button>
          )}
        </div>
        <p className="text-[11px] text-ink-muted text-center">
          {format === "story"
            ? <>Pick <b>Instagram → Story</b> or <b>WhatsApp → My status</b> in the share menu.</>
            : <>Pick <b>Instagram → Feed</b> in the share menu, then paste the copied caption.</>}
        </p>
      </div>
    </Sheet>
  );
}

/** The Instagram-gradient button that opens the sheet. */
export function InstagramButton({ onClick, label = "Instagram", size = "sm", className, "data-testid": testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("px-3 text-white flex items-center justify-center gap-1.5 text-sm font-semibold btn-press",
        size === "lg" ? "h-11 rounded-2xl w-full" : "h-9 rounded-xl",
        "bg-[linear-gradient(45deg,#F58529,#DD2A7B_45%,#8134AF_75%,#515BD4)]", className)}
      data-testid={testid}
    >
      <Instagram size={15} /> {label}
    </button>
  );
}
